/**
 * Base API client with retry logic and tenant-scoping
 * Task 29: Updated to support tenant-specific endpoints
 */

import axios, { AxiosInstance, AxiosError, AxiosRequestConfig } from 'axios';
import type { TenantDeploymentConfig } from '../types/tenant-config';

export class APIClient {
  private client: AxiosInstance;
  private apiKey: string | null = null;
  private tenantId: string | null = null;
  private baseURL: string;
  private tenantConfig: TenantDeploymentConfig | null = null;

  constructor(baseURL: string, timeout: number = 120000) {
    this.baseURL = baseURL;
    
    // Detect if on-prem deployment (longer timeouts for on-prem)
    const isOnPrem = this._isOnPremDeployment(baseURL);
    const effectiveTimeout = isOnPrem ? Math.max(timeout, 300000) : timeout; // At least 5 minutes for on-prem
    
    this.client = axios.create({
      baseURL,
      timeout: effectiveTimeout,
      headers: {
        'Content-Type': 'application/json',
      },
      // Disable credentials for CORS compatibility when using wildcard origins
      // Backend has allow_credentials=True which requires explicit origins, not "*"
      withCredentials: false,
    });

    // Request interceptor
    this.client.interceptors.request.use(
      (config) => {
        // Log ALL requests in development (including OPTIONS)
        if (import.meta.env.DEV) {
          const fullURL = `${this.baseURL}${config.url}`;
          const params = config.params ? `?${new URLSearchParams(config.params as any).toString()}` : '';
          console.log(`[API Client] ${config.method?.toUpperCase()} request:`, {
            url: config.url,
            fullURL: fullURL + params,
            params: config.params,
            hasAuth: !!this.apiKey,
            method: config.method,
          });
        }
        
        if (this.apiKey) {
          config.headers.Authorization = `Bearer ${this.apiKey}`;
          // Debug logging in development
          if (import.meta.env.DEV) {
            const tokenPreview = this.apiKey.length > 30 ? this.apiKey.substring(0, 30) + '...' : this.apiKey;
            console.debug(`[API Client] Setting Authorization header for ${config.method?.toUpperCase()} ${config.url}`, {
              tokenPreview,
              hasToken: !!this.apiKey,
              tokenLength: this.apiKey.length
            });
          }
        } else {
          // Warn if no auth token is set for protected endpoints
          if (import.meta.env.DEV && config.url && !config.url.includes('/health') && !config.url.includes('/ping')) {
            console.warn(`[API Client] No auth token set for ${config.method?.toUpperCase()} ${config.url}`);
          }
        }
        
        // Add tenant ID header if set
        if (this.tenantId) {
          config.headers['X-Tenant-ID'] = this.tenantId;
        }
        
        return config;
      },
      (error) => {
        if (import.meta.env.DEV) {
          console.error('[API Client] Request interceptor error:', error);
        }
        return Promise.reject(error);
      }
    );

    // Response interceptor
    this.client.interceptors.response.use(
      (response) => {
        // Log successful responses in development
        if (import.meta.env.DEV) {
          console.log(`[API Client] ${response.config.method?.toUpperCase()} response:`, {
            url: response.config.url,
            status: response.status,
            statusText: response.statusText,
            dataLength: Array.isArray(response.data) ? response.data.length : 'object',
          });
        }
        return response;
      },
      async (error: AxiosError) => {
        // Log ALL errors in development
        if (import.meta.env.DEV) {
          console.error('[API Client] Response error:', {
            url: error.config?.url,
            method: error.config?.method,
            status: error.response?.status,
            statusText: error.response?.statusText,
            message: error.message,
            code: error.code,
            response: error.response?.data,
          });
        }
        const config = error.config as AxiosRequestConfig & { _retry?: boolean; _retryCount?: number };
        const isOnPrem = this._isOnPremDeployment(this.baseURL);
        const maxRetries = isOnPrem ? 5 : 3; // More retries for on-prem
        const retryCount = config._retryCount || 0;
        
        // Retry on 429 or 5xx errors
        if (
          config &&
          retryCount < maxRetries &&
          error.response &&
          (error.response.status === 429 ||
            error.response.status >= 500)
        ) {
          config._retry = true;
          config._retryCount = retryCount + 1;
          
          // Exponential backoff with longer delays for on-prem
          const baseDelay = error.response.status === 429 ? 2000 : 1000;
          const delay = baseDelay * Math.pow(2, retryCount); // Exponential: 1s, 2s, 4s, 8s, 16s
          const maxDelay = isOnPrem ? 30000 : 10000; // Cap at 30s for on-prem, 10s for cloud
          const effectiveDelay = Math.min(delay, maxDelay);
          
          if (import.meta.env.DEV) {
            console.warn(
              `[API Client] Retrying request (${retryCount + 1}/${maxRetries}) after ${effectiveDelay}ms. ` +
              `Error: ${error.response.status} ${error.response.statusText}`
            );
          }
          
          await new Promise(resolve => setTimeout(resolve, effectiveDelay));
          
          return this.client(config);
        }
        
        // Enhanced error messages for on-prem
        if (isOnPrem && error.code === 'ECONNABORTED') {
          error.message = `Request timeout (on-prem deployment). The server may be slow or unreachable. ` +
            `Please check your network connection and server status.`;
        } else if (isOnPrem && !error.response) {
          error.message = `Network error (on-prem deployment). Unable to reach the API server. ` +
            `Please verify the API URL is correct and the server is running.`;
        }
        
        return Promise.reject(error);
      }
    );
  }

  setApiKey(apiKey: string) {
    // Trim whitespace from token
    this.apiKey = apiKey?.trim() || null;
    if (import.meta.env.DEV) {
      if (this.apiKey) {
        console.log('[API Client] API key set:', this.apiKey.substring(0, 20) + '...', '(length:', this.apiKey.length + ')');
      } else {
        console.warn('[API Client] Attempted to set empty API key');
      }
    }
  }

  /**
   * Check if deployment is on-prem based on base URL
   */
  private _isOnPremDeployment(baseURL: string): boolean {
    if (!baseURL) return false;
    
    // Check for localhost or local IPs
    if (baseURL.includes('localhost') || 
        baseURL.includes('127.0.0.1') ||
        /192\.168\./.test(baseURL) ||
        /^10\./.test(baseURL) ||
        /^172\.(1[6-9]|2[0-9]|3[0-1])\./.test(baseURL)) {
      return true;
    }
    
    // Check for internal domains
    if (/\.local/.test(baseURL) || 
        /\.internal/.test(baseURL) || 
        /\.lan/.test(baseURL) ||
        /\.corp/.test(baseURL)) {
      return true;
    }
    
    // Not a cloud domain
    if (!/\.(alpyne\.ai|azure|aws|gcp|cloud)/.test(baseURL)) {
      // Could be on-prem, but be conservative
      return false;
    }
    
    return false;
  }

  setAccessToken(token: string) {
    // Trim whitespace from token and use same field for JWT tokens
    this.apiKey = token?.trim() || null;
    if (import.meta.env.DEV) {
      if (this.apiKey) {
        console.log('[API Client] Access token set:', this.apiKey.substring(0, 20) + '...', '(length:', this.apiKey.length + ')');
      } else {
        console.warn('[API Client] Attempted to set empty access token');
      }
    }
  }

  clearAuth() {
    if (import.meta.env.DEV && this.apiKey) {
      console.warn('[API Client] Clearing authentication token');
      // Log stack trace in dev to see who's calling clearAuth
      if (import.meta.env.DEV) {
        console.trace('[API Client] clearAuth called from:');
      }
    }
    this.apiKey = null;
  }

  hasAuth(): boolean {
    const hasAuth = !!this.apiKey;
    if (import.meta.env.DEV) {
      console.debug('[API Client] hasAuth() called:', {
        hasAuth,
        apiKeyLength: this.apiKey?.length || 0,
        apiKeyPreview: this.apiKey ? this.apiKey.substring(0, 20) + '...' : 'none',
      });
    }
    return hasAuth;
  }

  getAuthToken(): string | null {
    return this.apiKey;
  }

  setTenantId(tenantId: string | null) {
    this.tenantId = tenantId;
  }

  getTenantId(): string | null {
    return this.tenantId;
  }

  /**
   * Set tenant configuration (Phase 3 - Task 29)
   * Updates request headers and potentially base URL based on tenant config.
   */
  setTenantConfig(config: TenantDeploymentConfig | null) {
    this.tenantConfig = config;
    
    // If tenant has a custom API endpoint, use it (for future use)
    // For now, we just store the config for header customization
    if (import.meta.env.DEV && config) {
      console.log('[API Client] Tenant config set:', {
        deploymentType: config.deploymentType,
        cloudProvider: config.cloudProvider,
        isActive: config.isActive,
      });
    }
  }

  getTenantConfig(): TenantDeploymentConfig | null {
    return this.tenantConfig;
  }

  /**
   * Check if tenant uses custom resources for a specific service.
   */
  tenantUsesCustom(resource: 'database' | 'storage' | 'redis' | 'openai'): boolean {
    if (!this.tenantConfig || !this.tenantConfig.isActive) {
      return false;
    }
    return this.tenantConfig.resourceStatus?.[resource] === 'custom';
  }

  /**
   * Get deployment type for current tenant.
   */
  getDeploymentType(): string {
    return this.tenantConfig?.deploymentType || 'shared';
  }

  async get<T = any>(url: string, config?: AxiosRequestConfig): Promise<T> {
    // Debug logging in development
    if (import.meta.env.DEV) {
      const fullURL = `${this.baseURL}${url}`;
      const params = config?.params ? `?${new URLSearchParams(config.params as any).toString()}` : '';
      console.log('[API Client] GET request:', {
        baseURL: this.baseURL,
        url,
        fullURL: fullURL + params,
        params: config?.params,
        hasAuth: !!this.apiKey,
        headers: config?.headers,
      });
    }
    
    // Add timeout to prevent hanging (30 seconds for on-prem, 10 seconds for cloud)
    const isOnPrem = this._isOnPremDeployment(this.baseURL);
    const timeout = isOnPrem ? 30000 : 10000;
    const requestConfig = {
      ...config,
      timeout: config?.timeout || timeout,
    };
    
    if (import.meta.env.DEV) {
      console.log('[API Client] GET request starting with timeout:', timeout + 'ms');
    }
    
    try {
      const response = await this.client.get<T>(url, requestConfig);
      if (import.meta.env.DEV) {
        console.log('[API Client] GET response:', {
          url,
          status: response.status,
          statusText: response.statusText,
          dataLength: Array.isArray(response.data) ? response.data.length : 'object',
        });
      }
      return response.data;
    } catch (error: any) {
      if (import.meta.env.DEV) {
        console.error('[API Client] GET error:', {
          url,
          status: error?.response?.status,
          statusText: error?.response?.statusText,
          message: error?.message,
          code: error?.code,
          response: error?.response?.data,
          timeout: error?.code === 'ECONNABORTED' || error?.message?.includes('timeout'),
          isTimeout: error?.code === 'ECONNABORTED',
        });
      }
      throw error;
    }
  }

  async post<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    // Debug logging in development
    if (import.meta.env.DEV) {
      const fullURL = `${this.baseURL}${url}`;
      console.log('[API Client] POST request:', {
        baseURL: this.baseURL,
        url,
        fullURL,
        hasAuth: !!this.apiKey,
      });
    }
    
    try {
      const response = await this.client.post<T>(url, data, config);
      
      if (import.meta.env.DEV) {
        console.log('[API Client] POST response:', {
          url,
          status: response.status,
          statusText: response.statusText,
          dataType: typeof response.data,
          dataKeys: response.data && typeof response.data === 'object' ? Object.keys(response.data) : 'N/A',
          hasData: !!response.data,
        });
      }
      
      return response.data;
    } catch (error: any) {
      if (import.meta.env.DEV) {
        console.error('[API Client] POST error:', {
          url,
          status: error?.response?.status,
          statusText: error?.response?.statusText,
          message: error?.message,
          responseData: error?.response?.data,
        });
      }
      throw error;
    }
  }

  async put<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.put<T>(url, data, config);
    return response.data;
  }

  async patch<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.patch<T>(url, data, config);
    return response.data;
  }

  async delete<T = any>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.delete<T>(url, config);
    return response.data;
  }

  // For file uploads
  async upload<T = any>(url: string, formData: FormData, onProgress?: (progress: number) => void): Promise<T> {
    const response = await this.client.post<T>(url, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress: (progressEvent) => {
        if (onProgress && progressEvent.total) {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          onProgress(percentCompleted);
        }
      },
    });
    return response.data;
  }

  /**
   * Health check - test API connectivity
   * Returns true if API is reachable, false otherwise
   */
  async healthCheck(): Promise<boolean> {
    try {
      const response = await this.client.get('/health', {
        timeout: 10000, // 10 second timeout for health check
      });
      return response.status === 200;
    } catch (error) {
      if (import.meta.env.DEV) {
        console.warn('[API Client] Health check failed:', error);
      }
      return false;
    }
  }

  /**
   * Ping endpoint - lightweight connectivity test
   * Returns true if API responds, false otherwise
   */
  async ping(): Promise<boolean> {
    try {
      const response = await this.client.get('/ping', {
        timeout: 5000, // 5 second timeout for ping
      });
      return response.status === 200;
    } catch (error) {
      if (import.meta.env.DEV) {
        console.warn('[API Client] Ping failed:', error);
      }
      return false;
    }
  }
}

// Global API client instance
let apiClient: APIClient | null = null;

export function initializeAPIClient(baseURL: string, apiKey?: string): APIClient {
  apiClient = new APIClient(baseURL);
  if (apiKey) {
    apiClient.setApiKey(apiKey);
  }
  return apiClient;
}

export function getAPIClient(): APIClient {
  if (!apiClient) {
    throw new Error('API client not initialized. Call initializeAPIClient first.');
  }
  return apiClient;
}

/**
 * Get the base URL for direct fetch calls (e.g., streaming)
 */
export function getAPIBaseUrl(): string {
  if (!apiClient) {
    throw new Error('API client not initialized. Call initializeAPIClient first.');
  }
  return (apiClient as any).baseURL;
}

/**
 * Get authorization headers for direct fetch calls (e.g., streaming)
 */
export function getAuthHeaders(): Record<string, string> {
  if (!apiClient) {
    throw new Error('API client not initialized. Call initializeAPIClient first.');
  }
  const headers: Record<string, string> = {};
  const apiKey = (apiClient as any).apiKey;
  const tenantId = (apiClient as any).tenantId;
  
  if (apiKey) {
    headers['Authorization'] = `Bearer ${apiKey}`;
  }
  if (tenantId) {
    headers['X-Tenant-ID'] = tenantId;
  }
  return headers;
}


